import React, { memo, useCallback } from 'react';
import styled from 'styled-components';
import { ReadMode, StackPanel, Typography, typographyPresets, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { toCamelCasedKeys, toTitleCasedKeys } from '@agilepoint/utils-common';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { TabFooterButtons } from '../tab-footer-buttons';
import { AppSelecionWrapper, StyledListView } from 'modules/create-new-app/components/app-wizard/styled-components';
import { IGetTextData, ISaveTextDataResponse } from 'modules/shared/queries';
import { ClearAppListContainer } from 'modules/default-app-settings/containers';
import { commonStyles, CustomFileUpload, getCurrentDate, GridLayout, IllustrationMessage, StyledStackPanel } from 'common';
import { useDefaultSettingsContext } from '../app-settings-layout';

export interface IAppMetadata {
    appID: string;
    description: string;
    guid: number;
    name: string;
    value: string;
}

interface IAppListProps {
    appMetadata: IAppMetadata[];
    restAPIData: Omit<IGetTextData, 'TextData'>
    mutationLoading: boolean;
    onSaveAppListDetails: (args: IGetTextData) => Promise<ISaveTextDataResponse>
}

interface IFileInfo {
    name: string;
    content?: string;
    error?: string;
}

const StyledFileUploadButton = styled(CustomFileUpload)`
    width: auto;
    height: auto;
    .ap-icon {
        margin-right: 10px;
    }
    .ap-button-label {
        margin: 0;
        ${typographyPresets.headings.h5};
    }
`;

const StyledGridLayout = styled(GridLayout)`
    color: ${({ theme: { mobileAppAccelerator } }) => mobileAppAccelerator.semanticColors.grayVariant2};
`;

const StyledText = styled(Typography.Medium)`
    ${commonStyles.textOverflow};  
`;

function getFileInfo(file: File, readMode: ReadMode): Promise<IFileInfo> {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.addEventListener('load', () => {
            resolve({
                content: fileReader.result as string,
                name: file.name
            });
        });
        fileReader.addEventListener('error', () => reject({
            error: 'Failed to read content.',
            name: file.name
        }));
        fileReader[readMode](file);
    });
}

const validateFileContent = (fileContent: string) => {
    try {
        const parsedContent = JSON.parse(fileContent) as { [key: string]: Array<{ Name: string, Value: string, Description: string, AppID: string }> };
        let isValid = false,
            content: { Name: string; Value: string; Description: string; AppID: string; }[] = [];
        for (const [key, value] of Object.entries(parsedContent)) {
            if (key === 'ApplicationList') {
                isValid = value.length > 0;
                content = value;
            }
            else isValid = false;
        }
        return { isValid, content };
    } catch (error) {
        return { isValid: false, content: [] };
    }
};

export const AppList = memo((props: IAppListProps) => {
    const { appMetadata, restAPIData, mutationLoading, onSaveAppListDetails } = props;
    const [appDisplaydata, setAppDisplayData] = React.useState(appMetadata);
    const { showNotifications } = useNotificationEvents();
    const { defaultSettingsConfig, setDefaultSettingsConfig } = useDefaultSettingsContext();
    const translate = useTranslate();

    const onFileUpload = useCallback(async (files: File[]) => {
        const [selectedFile] = files;
        if (selectedFile.type === 'application/json') {
            const fileInfo = await getFileInfo(selectedFile, 'readAsText');
            const { content, isValid } = validateFileContent(fileInfo.content!);
            if (isValid) {
                const uploadedFileData = content.map((item: any) => toCamelCasedKeys(item));
                return setAppDisplayData(uploadedFileData);
            }
        }
        showNotifications({ message: translate('modules.defaultAppSettings.appList.fileValidateText'), type: 'error' });
    }, [showNotifications, translate]);

    const onRenderItem = useCallback((itemProps: IAppMetadata) => <AppDetails {...itemProps} />, []);

    const onSubmitClickHandler = useCallback(() => {
        onSaveAppListDetails({
            ...restAPIData,
            CreatedDate: getCurrentDate(),
            TextData: JSON.stringify(appDisplaydata.map((app) => toTitleCasedKeys(app)))
        })
            .then(() => {
                showNotifications({ message: translate('modules.defaultAppSettings.appList.savedListDetails'), type: 'success' });
                setDefaultSettingsConfig({
                    doesNotIncludesAppListConfig: !appDisplaydata.length,
                    doesNotIncludesNotificationList: defaultSettingsConfig.doesNotIncludesNotificationList
                });
            })
            .catch(() => showNotifications({ message: translate('modules.defaultAppSettings.appList.failedListText'), type: 'error' }));
    }, [onSaveAppListDetails, restAPIData, appDisplaydata, showNotifications, translate, setDefaultSettingsConfig,
        defaultSettingsConfig.doesNotIncludesNotificationList]);

    const onClearInternalState = useCallback(() => {
        setAppDisplayData([]);
    }, []);

    return (
        <>
            <StyledStackPanel margin='30px 0px 0px 0px' alignment="center" width="100%" orientation='vertical' gap="30" height='calc(100% - 60px)' maxHeight='calc(100% - 107px)'>
                <Typography.H2 title={translate('modules.defaultAppSettings.appList.importListofAppsHeader')}><Localize translate='modules.defaultAppSettings.appList.importListofAppsHeader' /></Typography.H2>
                <StackPanel gap="15" orientation='vertical' height='100%'>
                    <StackPanel width="660px" justifyContent="flex-end">
                        <StyledFileUploadButton label={translate('modules.defaultAppSettings.appList.selectFileLabel')} accept='.json' onValueChange={onFileUpload} />
                        {appDisplaydata.length === 0 ? <></> : <ClearAppListContainer onClearInternalState={onClearInternalState} />}
                    </StackPanel>
                    <AppSelecionWrapper height='calc(100% - 115px)' $padding='25px 30px 30px 30px' flexDirection='column' maxHeight="620px" width="660px">
                        <StyledGridLayout $gridTemplateColumns="50% 50%" $alignItems="center">
                            <Typography.Small title={translate('modules.defaultAppSettings.appList.displayNameLabel')}>
                                <Localize translate='modules.defaultAppSettings.appList.displayNameLabel' />
                            </Typography.Small>
                            <Typography.Small title={translate('modules.defaultAppSettings.appList.appNameLabel')}>
                                <Localize translate='modules.defaultAppSettings.appList.appNameLabel' />
                            </Typography.Small>
                        </StyledGridLayout>
                        {appDisplaydata.length ?
                            <StyledListView
                                items={appDisplaydata}
                                onRenderItemContent={onRenderItem}
                                isVirtualized
                                allowKeyboardNavigation
                                itemHeight={45}
                                getKey={(item: IAppMetadata) => item.appID} />
                            :
                            <IllustrationMessage type="ap-empty-3" height='calc(100% - 40px)' displayMessage={translate('modules.defaultAppSettings.appList.displayText')} />}
                    </AppSelecionWrapper>
                </StackPanel>
            </StyledStackPanel>
            <TabFooterButtons onSubmitClickHandler={onSubmitClickHandler} mutationLoading={mutationLoading} />
        </>
    );
});

const AppDetails = React.memo((props: IAppMetadata) => {
    const { name } = props;
    return (
        <GridLayout $gridTemplateColumns="50% 50%" $alignItems="center" $height="100%">
            <StyledText title={name}>{name}</StyledText>
            <StyledText title={name}>{name}</StyledText>
        </GridLayout>
    );
});
